import React, { createContext, useContext, useReducer, useEffect } from 'react'

const STORAGE_KEY = 'cache-smash-mvp-v1'

const defaultState = {
  currentTab: 'home',
  playMode: null,
  multiplayerView: 'menu',
  multiplayerRegion: 'auto',
  multiplayerLobby: null,
  multiplayerInviteCode: null,
  multiplayerJoinCode: '',
  lobby: null, // Current lobby object
  currentMatch: null, // Current online match object
  currentUserId: null,
  currentStageId: 'battlefield',
  users: [],
  characters: [],
  tournaments: [],
  selectedCharacterId: null,
  selectedCpuCharacterId: null,
  matchHistory: [],
  killCams: [],
}

const characterArchetypes = {
  flameKnight: { label: 'Flame Knight', role: 'Close range', description: 'A fiery warrior wielding a blazing sword. Heavy impact with burning attacks.', stats: { power: 9, speed: 5, jump: 4, defense: 7 }, specialName: 'Inferno Slash', maxJumps: 1, component: 'FlameKnight' },
  shadowNinja: { label: 'Shadow Ninja', role: 'Mid range', description: 'A stealthy assassin emerging from darkness. Fast strikes from the shadows.', stats: { power: 6, speed: 8, jump: 6, defense: 4 }, specialName: 'Shadow Strike', maxJumps: 2, component: 'ShadowNinja' },
  cyberMech: { label: 'Cyber Mech', role: 'Front line', description: 'A heavily armored combat mech with energy weapons. Slow but devastating.', stats: { power: 8, speed: 3, jump: 4, defense: 9 }, specialName: 'Plasma Cannon', maxJumps: 1, component: 'CyberMech' },
  forestDruid: { label: 'Forest Druid', role: 'Balanced', description: 'A nature guardian wielding ancient magic. Versatile and resilient.', stats: { power: 7, speed: 6, jump: 5, defense: 6 }, specialName: 'Nature\'s Wrath', maxJumps: 2, component: 'ForestDruid' },
  stormMonk: { label: 'Storm Monk', role: 'Speedster', description: 'A lightning-fast martial artist channeling storm energy. Unmatched agility.', stats: { power: 6, speed: 9, jump: 7, defense: 4 }, specialName: 'Thunder Fist', maxJumps: 2, component: 'StormMonk' },
}



const stagePresets = {
  neonPier: {
    id: 'neonPier',
    name: 'Neon City',
    label: 'Cyberpunk',
    description: 'A neon-lit rooftop arena in a futuristic metropolis with flying cars and holographic billboards.',
    // Main platform: translate(200, 450), path from x=20 to x=780 relative
    // Absolute: x=220 to x=980, y=450
    left: 220,
    right: 980,
    floor: 450,
    ceiling: 0,
    blastBottom: 700,
    spawnLeft: 400,
    spawnRight: 800,
    spawnY: 350,
    skyTop: '#080414',
    skyBottom: '#1a103c',
    accent: 'rgba(0, 255, 255, 0.5)',
    platformTint: 'rgba(55, 65, 81, 0.5)',
    floorTint: 'rgba(31, 41, 55, 0.8)',
    // Platforms: translate(150, 280), (800, 280), (475, 150)
    // Each platform path: x=10 to x=240 relative
    platforms: [
      { x: 160, y: 280, width: 230, height: 10 },
      { x: 810, y: 280, width: 230, height: 10 },
      { x: 485, y: 150, width: 230, height: 10 },
    ],
  },
  skySpire: {
    id: 'skySpire',
    name: 'Ancient Temple',
    label: 'Sunset Ruins',
    description: 'Mystical temple ruins at sunset with stone pillars, fireflies, and overgrown vines.',
    // Main platform: translate(150, 450), path x=60 to x=840 relative
    // Absolute: x=210 to x=990, y=450
    left: 210,
    right: 990,
    floor: 450,
    ceiling: 0,
    blastBottom: 700,
    spawnLeft: 400,
    spawnRight: 800,
    spawnY: 350,
    skyTop: '#d946ef',
    skyBottom: '#fef08a',
    accent: 'rgba(245, 158, 11, 0.5)',
    platformTint: 'rgba(168, 162, 158, 0.5)',
    floorTint: 'rgba(214, 211, 209, 0.7)',
    platforms: [
      { x: 200, y: 250, width: 220, height: 10 },
      { x: 800, y: 250, width: 220, height: 10 },
      { x: 510, y: 120, width: 220, height: 10 },
    ],
  },
  reactorCore: {
    id: 'reactorCore',
    name: 'Volcanic Arena',
    label: 'Magma Chamber',
    description: 'A hellish volcanic arena with flowing lava, erupting volcanoes, and floating embers.',
    // Main platform: translate(250, 400), path x=40 to x=660 relative
    // Absolute: x=290 to x=910, y=400
    left: 290,
    right: 910,
    floor: 400,
    ceiling: 0,
    blastBottom: 650,
    spawnLeft: 450,
    spawnRight: 750,
    spawnY: 300,
    skyTop: '#1a0505',
    skyBottom: '#ff5722',
    accent: 'rgba(255, 152, 0, 0.6)',
    platformTint: 'rgba(58, 58, 58, 0.7)',
    floorTint: 'rgba(34, 34, 34, 0.9)',
    platforms: [
      { x: 150, y: 250, width: 230, height: 10 },
      { x: 800, y: 250, width: 230, height: 10 },
      { x: 495, y: 120, width: 230, height: 10 },
    ],
  },
  ironRing: {
    id: 'ironRing',
    name: 'Space Station',
    label: 'Orbital Platform',
    description: 'A high-tech space station with Earth in the background, energy beams, and floating debris.',
    // Main platform: translate(150, 450), path x=80 to x=820 relative
    // Absolute: x=230 to x=970, y=450
    left: 230,
    right: 970,
    floor: 450,
    ceiling: 0,
    blastBottom: 700,
    spawnLeft: 420,
    spawnRight: 780,
    spawnY: 350,
    skyTop: '#02010a',
    skyBottom: '#1a0b2e',
    accent: 'rgba(56, 189, 248, 0.6)',
    platformTint: 'rgba(241, 245, 249, 0.5)',
    floorTint: 'rgba(203, 213, 225, 0.7)',
    platforms: [
      { x: 200, y: 280, width: 240, height: 10 },
      { x: 750, y: 280, width: 240, height: 10 },
      { x: 475, y: 120, width: 240, height: 10 },
    ],
  },
  battlefield: {
    id: 'battlefield',
    name: 'Battlefield',
    label: 'Natural Arena',
    description: 'A scenic grassy plateau with mountains, waterfalls, and floating islands.',
    // Main platform: translate(200, 420), path x=40 to x=760 relative
    // Absolute: x=240 to x=960, y=420
    left: 240,
    right: 960,
    floor: 420,
    ceiling: 0,
    blastBottom: 670,
    spawnLeft: 420,
    spawnRight: 780,
    spawnY: 320,
    skyTop: '#1a8cff',
    skyBottom: '#a1e4ff',
    accent: 'rgba(104, 211, 92, 0.5)',
    platformTint: 'rgba(72, 187, 60, 0.6)',
    floorTint: 'rgba(92, 58, 33, 0.8)',
    platforms: [
      { x: 180, y: 260, width: 240, height: 10 },
      { x: 780, y: 260, width: 240, height: 10 },
      { x: 480, y: 120, width: 240, height: 10 },
    ],
  },
}

const tabs = [
  ['home', 'Home'],
  ['play', 'Play'],
  ['stages', 'Stages'],
  ['characters', 'Characters'],
  ['tournaments', 'Tournaments'],
  ['profile', 'Profile'],
]

const AppContext = createContext(null)

// Map old archetypes to new ones
const archetypeMigration = {
  brawler: 'flameKnight',
  ranger: 'shadowNinja',
  striker: 'forestDruid',
  tank: 'cyberMech',
}

function normalizeCharacter(character) {
  // Migrate old archetype names to new ones
  const migratedArchetype = archetypeMigration[character.archetype] || character.archetype
  const archetype = characterArchetypes[migratedArchetype] || null
  
  const inferredMaxJumps =
    typeof character.maxJumps === 'number'
      ? character.maxJumps
      : archetype?.maxJumps ?? 2
  
  // Reconstruct stats object from flat properties or archetype
  const stats = character.stats || {
    power: character.power ?? archetype?.stats?.power ?? 5,
    speed: character.speed ?? archetype?.stats?.speed ?? 5,
    jump: character.jump ?? archetype?.stats?.jump ?? 5,
    defense: character.defense ?? archetype?.stats?.defense ?? 5,
  }
  
  return {
    ...character,
    archetype: migratedArchetype,
    maxJumps: inferredMaxJumps,
    stats,
    specialName: character.specialName ?? archetype?.specialName ?? 'Special',
  }
}

function normalizeState(nextState) {
  return {
    ...nextState,
    currentStageId: nextState.currentStageId || defaultState.currentStageId,
    playMode: nextState.playMode || null,
    multiplayerView: nextState.multiplayerView || 'menu',
    multiplayerRegion: nextState.multiplayerRegion || 'auto',
    multiplayerLobby: nextState.multiplayerLobby || null,
    multiplayerInviteCode: nextState.multiplayerInviteCode || null,
    multiplayerJoinCode: nextState.multiplayerJoinCode || '',
    selectedCpuCharacterId: nextState.selectedCpuCharacterId || null,
    characters: Array.isArray(nextState.characters) ? nextState.characters.map(normalizeCharacter) : [],
    killCams: Array.isArray(nextState.killCams) ? nextState.killCams : [],
  }
}

function migrateOldData(data) {
  // Migrate from vanilla JS format to React format
  const migrated = { ...data }
  
  // Migrate users: displayName -> name
  if (Array.isArray(migrated.users)) {
    migrated.users = migrated.users.map(u => ({
      ...u,
      name: u.name || u.displayName || 'Unknown',
    }))
  }
  
  // Migrate characters: ensure ownerId is present
  if (Array.isArray(migrated.characters)) {
    migrated.characters = migrated.characters.map(c => ({
      ...c,
      ownerId: c.ownerId || migrated.currentUserId,
    }))
  }
  
  return migrated
}

function seededState() {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      const migrated = migrateOldData(parsed)
      return normalizeState({ ...defaultState, ...migrated })
    } catch {
      // Fall through to default
    }
  }
  return normalizeState({ ...defaultState })
}

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_TAB':
      return { ...state, currentTab: action.payload }
    case 'SET_STAGE':
      return { ...state, currentStageId: action.payload }
    case 'SET_PLAY_MODE':
      return { ...state, playMode: action.payload }
    case 'SET_CHARACTERS':
      return { ...state, characters: action.payload.map(normalizeCharacter) }
    case 'ADD_CHARACTER':
      return { ...state, characters: [...state.characters, normalizeCharacter(action.payload)] }
    case 'DELETE_CHARACTER':
      return { ...state, characters: state.characters.filter(c => c.id !== action.payload) }
    case 'SELECT_CHARACTER':
      return { ...state, selectedCharacterId: action.payload }
    case 'SELECT_CPU_CHARACTER':
      return { ...state, selectedCpuCharacterId: action.payload }
    case 'SET_USERS':
      return { ...state, users: action.payload }
    case 'ADD_USER':
      return { ...state, users: [...state.users, action.payload] }
    case 'SET_CURRENT_USER':
      return { ...state, currentUserId: action.payload }
    case 'SET_TOURNAMENTS':
      return { ...state, tournaments: action.payload }
    case 'ADD_TOURNAMENT':
      return { ...state, tournaments: [...state.tournaments, action.payload] }
    case 'SET_MULTIPLAYER_LOBBY':
      return { ...state, multiplayerLobby: action.payload }
    case 'SET_MULTIPLAYER_VIEW':
      return { ...state, multiplayerView: action.payload }
    case 'SET_LOBBY':
      return { ...state, lobby: action.payload }
    case 'SET_MATCH':
      return { ...state, currentMatch: action.payload }
    case 'SET_INVITE_CODE':
      return { ...state, multiplayerInviteCode: action.payload }
    case 'SET_JOIN_CODE':
      return { ...state, multiplayerJoinCode: action.payload }
    case 'ADD_MATCH':
      return { ...state, matchHistory: [action.payload, ...state.matchHistory] }
    case 'ADD_KILL_CAM':
      return { ...state, killCams: [action.payload, ...state.killCams].slice(0, 20) }
    case 'RESET_STATE':
      return seededState()
    default:
      return state
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, null, seededState)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state])

  const value = {
    state,
    dispatch,
    characterArchetypes,
    stagePresets,
    tabs,
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}
